import { useLingui } from "@lingui/react/macro";
import { Lock, LockOpen } from "lucide-react";
import { observer } from "mobx-react-lite";
import { Fragment, useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import { FeatureFlagUpdate, UserFeatureFlag } from "../../../api";
import { FeatureFlags } from "../../../api/user-feature-flags";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Button } from "../../../components/ui-kit/button";
import { BaseCheckbox } from "../../../components/ui-kit/fields/base-checkbox";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { groupFlags } from "../../../components/user-feature-flags";
import {
  userFeatureFlagHints,
  userFeatureFlags,
} from "../../../constants/enum-to-text";
import { PERMISSION_ROW_CLASS } from "../permissions/permissions-picker";

/**
 * A user's feature flags as rows of the permissions dialog, matching the
 * permission rows around them: the checkbox is the flag's value, the lock before
 * it whether the user may change that value themselves. Rendered into a
 * `PermissionSection`'s grid, one sub-heading per consumer.
 *
 * Each change saves on its own, outside the dialog's Save. Only admins open the
 * dialog, so every value and lock is editable here - the patient profile's
 * `UserFeatureFlags` is the one that works out what a caller may change.
 */
export const FeatureFlagsPicker = observer(function FeatureFlagsPicker(props: {
  flags: FeatureFlags;
  onSave: (flag: UserFeatureFlag, update: FeatureFlagUpdate) => Promise<void>;
}) {
  const { t } = useLingui();
  const [saving, setSaving] = useState<string | null>(null);

  const save = async (
    key: string,
    flag: UserFeatureFlag,
    update: FeatureFlagUpdate,
  ) => {
    setSaving(key);
    try {
      await props.onSave(flag, update);
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      {groupFlags(props.flags).map((group) => (
        <Fragment key={group.prefix}>
          <div className="text-muted-foreground col-span-full pt-2 pb-0.5 text-xs first:pt-0">
            {resolveEnumLabel(group.label)}
          </div>

          {group.flags.map(([flag, state]) => {
            const label = resolveEnumLabel(userFeatureFlags[flag]) || flag;
            const hint = resolveEnumLabel(userFeatureFlagHints[flag]);
            const userEditable = state.user_editable !== false;
            return (
              // `flex-nowrap`: a long label wraps its own text rather than
              // pushing the hint onto a line of its own.
              <div
                key={flag}
                className={cn(PERMISSION_ROW_CLASS, "flex-nowrap")}
              >
                {/* Before the checkbox, not at the row's far end, so it reads
                    as belonging to the flag beside it. */}
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={userEditable ? LockOpen : Lock}
                  label={t`User can edit: ${label}`}
                  tooltip={
                    userEditable ? t`User can edit` : t`Only staff can edit`
                  }
                  // Pressed while the user may change the flag, so the flags
                  // they can touch stand out from the staff-managed ones.
                  aria-pressed={userEditable}
                  disabled={saving === `${flag}:lock`}
                  className="shrink-0"
                  onClick={() => {
                    void save(`${flag}:lock`, flag, {
                      user_editable: !userEditable,
                    });
                  }}
                />
                <BaseCheckbox
                  labelClassName="min-w-0"
                  checked={state.value === true}
                  disabled={saving === `${flag}:value`}
                  aria-label={label}
                  onCheckedChange={(checked) => {
                    void save(`${flag}:value`, flag, {
                      value: checked === true,
                    });
                  }}
                  label={<span className="self-baseline">{label}</span>}
                />
                {/* Outside the label, so clicking it for the hint does not
                    toggle the flag. */}
                {hint && <HelperTooltip content={hint} className="shrink-0" />}
              </div>
            );
          })}
        </Fragment>
      ))}
    </>
  );
});
